import { create } from 'zustand'

const LAST_ORDER_KEY = 'admin-last-order-id'

function getLastOrderId(): number | null {
  try {
    const stored = localStorage.getItem(LAST_ORDER_KEY)
    return stored ? Number(stored) : null
  } catch {
    return null
  }
}

interface DashboardState {
  // Order selection
  selectedOrderId: number | null
  selectOrder: (id: number | null) => void

  // Event filter (timeline)
  eventFilter: string | null
  setEventFilter: (filter: string | null) => void

  // Table filters — dropdown (array)
  statusFilter: string[]
  setStatusFilter: (filter: string[]) => void
  employeeFilter: number[]
  setEmployeeFilter: (filter: number[]) => void
  templateFilter: number[]
  setTemplateFilter: (filter: number[]) => void

  // Table filters — date ranges
  orderDateFrom: string | null
  orderDateTo: string | null
  deliveryDateFrom: string | null
  deliveryDateTo: string | null
  setDateRange: (column: 'orderDate' | 'deliveryDate', from: string | null, to: string | null) => void

  // Table filters — number range
  orderIdFrom: number | null
  orderIdTo: number | null
  setOrderIdRange: (from: number | null, to: number | null) => void

  // Table filters — text (ILIKE)
  customerNameFilter: string
  referenceFilter: string
  yourRefFilter: string
  infoFilter: string
  phoneFilter: string
  emailFilter: string
  customerIdFilter: string
  deliveryNameFilter: string
  setTextFilter: (column: 'customerName' | 'reference' | 'yourRef' | 'info' | 'phone' | 'email' | 'customerId' | 'deliveryName', value: string) => void

  // Global search (tsvector full-text)
  searchQuery: string
  setSearchQuery: (q: string) => void

  // Clear all column filters
  clearAllFilters: () => void

  // Pagination — første/forrige/neste. Ingen sidetall og ingen «siste side»:
  // begge krever totalen, altså akkurat skannet vi fjernet (#934).
  pageIndex: number
  pageSize: number
  setPageIndex: (index: number) => void
  setPageSize: (size: number) => void

  // Totaler på forespørsel. Antall + sum skanner hele treffmengden, så de
  // hentes kun når operatøren ber om det — og nullstilles så snart filteret
  // endres, slik at et tall aldri står igjen og beskriver en annen mengde.
  totalsRequested: boolean
  requestTotals: () => void

  // Sorting
  sortColumn: string
  sortDirection: 'asc' | 'desc'
  setSorting: (column: string, direction: 'asc' | 'desc') => void
}

// Enhver filterendring gjør både gjeldende side og et hentet totaltall
// ugyldig. Å samle de to nullstillingene ett sted er det som holder dem
// i takt — spredt utover 8 settere ville den ene før eller siden bli glemt.
const FILTER_CHANGED = { pageIndex: 0, totalsRequested: false } as const

export const useDashboardStore = create<DashboardState>((set) => ({
  selectedOrderId: getLastOrderId(),
  selectOrder: (id) => {
    if (id !== null) {
      // localStorage can throw (private mode, quota) — remembering the last
      // order is a convenience, so a failure must not break selection.
      try { localStorage.setItem(LAST_ORDER_KEY, String(id)) } catch { /* ignore */ }
    }
    set({ selectedOrderId: id })
  },

  eventFilter: null,
  setEventFilter: (filter) => set({ eventFilter: filter }),

  statusFilter: [],
  setStatusFilter: (filter) => set({ statusFilter: filter, ...FILTER_CHANGED }),
  employeeFilter: [],
  setEmployeeFilter: (filter) => set({ employeeFilter: filter, ...FILTER_CHANGED }),
  templateFilter: [],
  setTemplateFilter: (filter) => set({ templateFilter: filter, ...FILTER_CHANGED }),

  orderDateFrom: null,
  orderDateTo: null,
  deliveryDateFrom: null,
  deliveryDateTo: null,
  setDateRange: (column, from, to) => {
    if (column === 'orderDate') {
      set({ orderDateFrom: from, orderDateTo: to, ...FILTER_CHANGED })
    } else {
      set({ deliveryDateFrom: from, deliveryDateTo: to, ...FILTER_CHANGED })
    }
  },

  orderIdFrom: null,
  orderIdTo: null,
  setOrderIdRange: (from, to) => set({ orderIdFrom: from, orderIdTo: to, ...FILTER_CHANGED }),

  customerNameFilter: '',
  referenceFilter: '',
  yourRefFilter: '',
  infoFilter: '',
  phoneFilter: '',
  emailFilter: '',
  customerIdFilter: '',
  deliveryNameFilter: '',
  setTextFilter: (column, value) => {
    const key = {
      customerName: 'customerNameFilter',
      reference: 'referenceFilter',
      yourRef: 'yourRefFilter',
      info: 'infoFilter',
      phone: 'phoneFilter',
      email: 'emailFilter',
      customerId: 'customerIdFilter',
      deliveryName: 'deliveryNameFilter',
    }[column] as string
    set({ [key]: value, ...FILTER_CHANGED })
  },

  searchQuery: '',
  setSearchQuery: (q) => set({ searchQuery: q, ...FILTER_CHANGED }),

  clearAllFilters: () =>
    set({
      statusFilter: [],
      employeeFilter: [],
      templateFilter: [],
      orderDateFrom: null,
      orderDateTo: null,
      deliveryDateFrom: null,
      deliveryDateTo: null,
      orderIdFrom: null,
      orderIdTo: null,
      customerNameFilter: '',
      referenceFilter: '',
      yourRefFilter: '',
      infoFilter: '',
      phoneFilter: '',
      emailFilter: '',
      customerIdFilter: '',
      deliveryNameFilter: '',
      searchQuery: '',
      ...FILTER_CHANGED,
    }),

  pageIndex: 0,
  pageSize: 25,
  setPageIndex: (index) => set({ pageIndex: Math.max(0, index) }),
  // Sidestørrelse og sortering endrer ikke hvilke rader som matcher, bare
  // hvordan de deles opp — et hentet totaltall er fortsatt gyldig.
  setPageSize: (size) => set({ pageSize: size, pageIndex: 0 }),

  totalsRequested: false,
  requestTotals: () => set({ totalsRequested: true }),

  sortColumn: 'order_date',
  sortDirection: 'desc',
  setSorting: (column, direction) => set({ sortColumn: column, sortDirection: direction, pageIndex: 0 }),
}))
